import { IntlShape } from "react-intl";
import { conditionRowMessages as messages } from "./conditionRows.messages";
import { B2BEntityDto, CommercialTermsRowDto } from "./infrastructure/B2BAdminApi";

const DASH = "—";

export interface ConditionRow {
  id: string;
  agency: string;
  company: string;
  commission: string;
  validity: string;
  properties: string;
  /** The row as listed, for master mode to edit or delete it. */
  source: CommercialTermsRowDto;
}

function formatDate(intl: IntlShape, isoDate?: string): string | undefined {
  // Date-only values: formatted in UTC so a negative offset does not show the previous day.
  return isoDate
    ? intl.formatDate(isoDate, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })
    : undefined;
}

function formatValidity(intl: IntlShape, row: CommercialTermsRowDto): string {
  const start = formatDate(intl, row.validFrom);
  const end = formatDate(intl, row.validTo);
  return start || end ? `${start ?? "…"} → ${end ?? "…"}` : DASH;
}

export function formatCommission(intl: IntlShape, row: CommercialTermsRowDto): string {
  if (row.net) {
    return intl.formatMessage(messages.net);
  }
  return typeof row.commissionPct === "number" ? `${intl.formatNumber(row.commissionPct)} %` : DASH;
}

export function buildConditionRows(
  terms: CommercialTermsRowDto[],
  entities: B2BEntityDto[],
  intl: IntlShape
): ConditionRow[] {
  const entityById = new Map(entities.map(entity => [entity.entityId, entity]));

  // Terms can outlive the entity they were pushed for; those price nothing, so they are left out.
  return terms.flatMap(row => {
    const entity = entityById.get(row.entityId);
    if (!entity) {
      return [];
    }
    const owner = entity.legalName || row.entityName;
    const counterparty = row.counterpartyName || DASH;
    const isCompanyProfile = entity.entityType === "company";
    const hotelCount = row.hotelCodes?.length ?? 0;
    return {
      id: `${row.entityId}:${row.agreementId ?? "default"}:${row.lineId ?? "default"}:${
        row.appliesTo ?? ""
      }`,
      agency: isCompanyProfile ? counterparty : owner,
      company: isCompanyProfile ? owner : counterparty,
      commission: formatCommission(intl, row),
      validity: formatValidity(intl, row),
      properties:
        hotelCount === 0
          ? intl.formatMessage(messages.allProperties)
          : intl.formatMessage(messages.someProperties, { count: hotelCount }),
      source: row,
    };
  });
}
